import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Expand, X, ArrowLeft, ArrowRight } from "lucide-react";
import { GALLERY } from "@/constants/testIds";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const projects = [
  {
    id: 1,
    title: "Historic Victorian — Full Perimeter RGB",
    caption: "Individually addressable pixels traced along every gable and eave. Homeowner scenes controlled from the Omni app in real-time.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/w9zp4jgm_3.webp",
    tag: "Residential · RGB",
  },
  {
    id: 2,
    title: "Bugsy's & Center Strip — Teal Wash",
    caption: "Full-length commercial strip in teal. Programmable to seasonal palettes and brand color changes on demand.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/sit4o3ny_Screenshot%202025-05-23%20at%203.31.56%E2%80%AFPM.png",
    tag: "Commercial",
  },
  {
    id: 3,
    title: "Lakeside Estate — Teal & Emerald Wrap",
    caption: "Two-story residential wrap. Cool-tone accent lighting balanced against warm interior glow.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/v02sx0h9_Untitled%20design-107.png",
    tag: "Residential · RGB",
  },
  {
    id: 4,
    title: "Country Ranch — Warm White Perimeter",
    caption: "Classic warm-white preset traces the full roofline on this expansive rural home. Same fixtures pivot to full color for holidays.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/u3ojd97q_Untitled%20design-116.png",
    tag: "Residential · Warm White",
  },
  {
    id: 5,
    title: "RushHour Karting — Signature Green",
    caption: "Commercial storefront in signature brand green. Backlit signage integrated with the perimeter Omni system.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/2trvhas5_Untitled%20design-153.png",
    tag: "Commercial · Signage",
  },
  {
    id: 6,
    title: "Downtown Strip Mall — Teal Detailing",
    caption: "Storefront strip lit head-to-toe in teal — clean, consistent, controlled from a single Omni hub.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/pkdu0j41_Untitled%20design-220.webp",
    tag: "Commercial",
  },
  {
    id: 7,
    title: "Modern Estate — Cool Chroma Wash",
    caption: "Cyan and violet zones wash a contemporary façade. Uplighting adds depth and pulls the architecture forward at night.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/jq83mccy_17.png",
    tag: "Residential · Landmark",
  },
  {
    id: 8,
    title: "Coastal Home — Warm White Preset",
    caption: "Classic warm white preset for everyday elegance. Same fixtures pivot to full-color for holidays with a single tap.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/t94mxy27_14.png",
    tag: "Residential · Coastal",
  },
  {
    id: 9,
    title: "Three-Car Garage — Rainbow Sequence",
    caption: "Zone-by-zone color mapping across a wide façade. Each fixture individually addressable for smooth chase animations.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/0uv1v77v_15.png",
    tag: "Residential · Statement",
  },
  {
    id: 10,
    title: "Same Home — Everyday White Mode",
    caption: "The same installation shown here in daily warm-white mode. Discreet by day, cinematic when you flip the scene.",
    src: "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/9jts7a5w_16.png",
    tag: "Residential · Everyday",
  },
];

export default function VideoGallery() {
  const [index, setIndex] = useState(null); // null | number
  const open = index !== null ? projects[index] : null;

  const next = (e) => { e?.stopPropagation(); setIndex((i) => (i === null ? 0 : (i + 1) % projects.length)); };
  const prev = (e) => { e?.stopPropagation(); setIndex((i) => (i === null ? 0 : (i - 1 + projects.length) % projects.length)); };

  return (
    <section id="gallery" data-testid={GALLERY.root} className="section-pad relative">
      <div className="container-x">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-20 mb-14">
          <div className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
            [ Gallery ]
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display uppercase text-white text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[0.95] tracking-tight max-w-3xl">
              Recent<br /><span className="text-white/40">installations.</span>
            </h2>
            <p className="text-white/60 max-w-sm">
              Real homes. Real installs. Every fixture on-brand and factory-warrantied. Tap any tile for the full frame.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((v, i) => (
            <motion.button
              key={v.id}
              data-testid={GALLERY.thumb(i)}
              onClick={() => setIndex(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="group text-left"
            >
              <div className="relative aspect-[4/3] overflow-hidden border border-white/10 bg-[#0C1017]">
                <img
                  src={v.src}
                  alt={v.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-opacity duration-500 group-hover:from-black/70" />
                <div className="absolute top-4 left-4">
                  <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white bg-black/60 backdrop-blur px-2 py-1 border border-white/10">
                    {v.tag}
                  </span>
                </div>
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/30 bg-black/40 backdrop-blur-md text-white">
                    <Expand size={14} />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display uppercase text-white text-xl md:text-2xl leading-tight tracking-tight">
                    {v.title}
                  </h3>
                </div>
              </div>
              <p className="mt-3 text-white/55 text-sm leading-relaxed">
                {v.caption}
              </p>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setIndex(null)}>
        <DialogContent
          data-testid={GALLERY.lightbox}
          className="max-w-6xl w-[96vw] bg-[#05070A] border border-white/10 p-0 overflow-hidden [&>button]:hidden"
        >
          <DialogTitle className="sr-only">{open?.title || "Project image"}</DialogTitle>
          <AnimatePresence mode="wait">
            {open && (
              <motion.div
                key={open.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                <div className="relative aspect-[16/10] bg-black">
                  <img
                    src={open.src}
                    alt={open.title}
                    className="w-full h-full object-contain"
                  />
                  <button
                    onClick={prev}
                    aria-label="Previous"
                    className="absolute top-1/2 -translate-y-1/2 left-3 w-10 h-10 flex items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[color:var(--primary)] hover:text-black transition-colors"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next"
                    className="absolute top-1/2 -translate-y-1/2 right-3 w-10 h-10 flex items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[color:var(--primary)] hover:text-black transition-colors"
                  >
                    <ArrowRight size={16} />
                  </button>
                </div>
                <button
                  data-testid={GALLERY.lightboxClose}
                  onClick={() => setIndex(null)}
                  className="absolute top-3 right-3 p-2 bg-black/60 border border-white/20 text-white hover:bg-[color:var(--primary)] hover:text-black transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <div className="p-6 md:p-8 border-t border-white/10 flex items-start justify-between gap-6">
                  <div>
                    <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[color:var(--primary)] mb-2">
                      {open.tag}
                    </div>
                    <h3 className="font-display uppercase text-white text-2xl md:text-3xl leading-tight tracking-tight">
                      {open.title}
                    </h3>
                    <p className="mt-3 text-white/60 text-sm max-w-2xl leading-relaxed">
                      {open.caption}
                    </p>
                  </div>
                  <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 whitespace-nowrap">
                    {String(index + 1).padStart(2, "0")} / {String(projects.length).padStart(2, "0")}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
}
